import { FC } from "react";
import { Divider, IconButton, SelectChangeEvent, Tooltip } from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  AddingCriteria,
  AdvancedDeclarationOption,
  AllotmentPolicies,
  EditAllotmentPoliciesLocationValues,
  LocationGrainTrackerValues,
  PrincipalInfoGT,
  QualityCriteriaData,
  StoragesTemplates,
  StorageTemplate,
} from "../../../../redux/slices/grainTracker/grainTrackerTypes";
import { PrincipalInformationGrainTracker } from "./grainWiseFormParts/PrincipalInformationGrainTracker";
import GrainWiseTonsToWheat from "../GrainWiseTonsPerSpecie";
import QualityCriteriaInformationGrainTracker from "./grainWiseFormParts/QualityCriteriaGrainTracker";
import { DataSBFilterValues } from "../../../../redux/actions/dataTable/DataTableTypes";

type GrainTrackerFormParms = {
  t: (arg0: string) => string;
  locationGrainTrackerValues: LocationGrainTrackerValues;
  theBinIsGoodValues: boolean;
  editBins: EditAllotmentPoliciesLocationValues;
  exitEditBinMode: (
    locationGrainTrackerValues: LocationGrainTrackerValues,
  ) => void;
  storage: string | undefined;
  handleStorageId: (event: SelectChangeEvent) => void;
  storagesTemplates: StoragesTemplates | undefined;
  name: string;
  setNamePIGT: (principalInfoGT: PrincipalInfoGT, name: string) => void;
  principalInfoGT: PrincipalInfoGT;
  specie: any;
  handleChangeSpecies: (event: SelectChangeEvent) => void;
  addOptionByDefault: {
    id: number;
    slug: string;
    textIdLocale: string;
  }[];
  tonsCapacity: number | undefined;
  setTonsPIGT: (principalInfoGT: PrincipalInfoGT, tons: number) => void;

  totalTons: number;
  selectedSpecie: StorageTemplate | undefined;

  qualityCriteria: {
    data: QualityCriteriaData[];
    total: number;
    limit: number;
    loading: boolean;
    error: string;
  };

  AllotmentPolicies: AllotmentPolicies[];
  qualityCriteriaData: QualityCriteriaData[];
  setCriteriaValuesToDelete: (values: string[]) => void;
  selectedCriteriaToDelete: string[];

  addingCriteria: AddingCriteria;
  selectedCriteriaV: (values: any) => void;
  setCriteriaToBin: (addingCriteria: AddingCriteria) => void;
  addOptionCriteriaByDefault: QualityCriteriaData[];

  selectedCriteriaTemplate: QualityCriteriaData[];

  resetAddingCriteria: () => void;
  setAdvancedDeclarationOptionText: (
    advancedDeclarationOption: AdvancedDeclarationOption,
    searchText: string,
  ) => void;
  advancedDeclarationOption: AdvancedDeclarationOption;
  setAvancedDeclarationOptionSelected: (values: {
    allValuesSelected: boolean;
    valuesSelected: DataSBFilterValues[];
  }) => void;
  advancedDeclarationOptionSelected: {
    allValuesSelected: boolean;
    valuesSelected: DataSBFilterValues[];
  };
  selectedCriteria: string | undefined;
};

const GrainTrackerFormWithCriteria: FC<GrainTrackerFormParms> = ({
  t,
  locationGrainTrackerValues,
  theBinIsGoodValues,
  editBins,
  exitEditBinMode,
  storage,
  handleStorageId,
  storagesTemplates,
  name,
  setNamePIGT,
  principalInfoGT,
  specie,
  handleChangeSpecies,
  addOptionByDefault,
  tonsCapacity,
  setTonsPIGT,
  totalTons,
  selectedSpecie,

  qualityCriteria,
  AllotmentPolicies,
  qualityCriteriaData,
  setCriteriaValuesToDelete,
  selectedCriteriaToDelete,
  addingCriteria,
  selectedCriteriaV,
  setCriteriaToBin,
  addOptionCriteriaByDefault,
  selectedCriteriaTemplate,
  resetAddingCriteria,
  setAdvancedDeclarationOptionText,
  advancedDeclarationOption,
  setAvancedDeclarationOptionSelected,
  advancedDeclarationOptionSelected,
  selectedCriteria,
}) => {
  return (
    <div className="form-grain-tracker-container">
      <div className="exit-edition-bin">
        <Tooltip title={t("Grain_tracker.exit_edition")} placement="right">
          <IconButton
            onClick={() => exitEditBinMode(locationGrainTrackerValues)}
            disabled={!theBinIsGoodValues || editBins.editedLoading}
          >
            <HighlightOffIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className="graintracker-first-information">
        <div className="exit-edition">
          <div className="info-title-graintracker">
            {t("Grain_tracker.info-title")}
          </div>
        </div>
        <PrincipalInformationGrainTracker
          t={t}
          storage={storage}
          AllotmentPolicies={AllotmentPolicies}
          handleStorageId={handleStorageId}
          storagesTemplates={storagesTemplates}
          name={name}
          setNamePIGT={setNamePIGT}
          principalInfoGT={principalInfoGT}
          specie={specie}
          handleChangeSpecies={handleChangeSpecies}
          addOptionByDefault={addOptionByDefault}
          tonsCapacity={tonsCapacity}
          setTonsPIGT={setTonsPIGT}
          editedLoading={editBins.editedLoading}
          selectedCriteriaToDelete={selectedCriteriaToDelete}
        />

        {totalTons === 0 ? (
          <div className="spacer-tons-no-soft" />
        ) : (
          <GrainWiseTonsToWheat
            t={t}
            selectedSpecie={selectedSpecie}
            totalTons={totalTons}
          />
        )}
      </div>
      <Divider />

      <QualityCriteriaInformationGrainTracker
        qualityCriteria={qualityCriteria}
        AllotmentPolicies={AllotmentPolicies}
        qualityCriteriaData={qualityCriteriaData}
        setCriteriaValuesToDelete={setCriteriaValuesToDelete}
        selectedCriteriaToDelete={selectedCriteriaToDelete}
        addingCriteria={addingCriteria}
        selectedCriteriaV={selectedCriteriaV}
        setCriteriaToBin={setCriteriaToBin}
        addOptionCriteriaByDefault={addOptionCriteriaByDefault}
        selectedCriteriaTemplate={selectedCriteriaTemplate}
        resetAddingCriteria={resetAddingCriteria}
        setAdvancedDeclarationOptionText={setAdvancedDeclarationOptionText}
        advancedDeclarationOption={advancedDeclarationOption}
        setAvancedDeclarationOptionSelected={
          setAvancedDeclarationOptionSelected
        }
        advancedDeclarationOptionSelected={advancedDeclarationOptionSelected}
        selectedCriteria={selectedCriteria}
        editBins={editBins}
        locationGrainTrackerValues={locationGrainTrackerValues}
        principalInfoGT={principalInfoGT}
        t={t}
      />
    </div>
  );
};

export default GrainTrackerFormWithCriteria;
